import {
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from "@angular/core";
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from "@angular/forms";
import {buildErrorMessage} from "../utils";
import {IControlWithErrors, NControlsValidationErrors, NInputErrorMessages} from "../public_components";
// noinspection TypeScriptPreferShortImport
import {ErrorMessagesFactory} from "../../services/error-messages-factory.service";
import {IAutoSizeComponent} from "../../directives/autoSize.directive";

@Component({
  selector: "n-input",
  styleUrls: [
    "./n-input.css"
  ],
  templateUrl: "./n-input.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NInputComponent),
      multi: true
    }
  ]
})
export class NInputComponent implements ControlValueAccessor, OnInit, IAutoSizeComponent, IControlWithErrors {
  /**
   * Input placeholder with field if empty
   */
  @Input() placeholder = "";
  @Input() theme: "" | "flat" = "";
  isClearable: boolean | "nofocus";
  /**
   * No trim value
   */
  @Input() noTrim: boolean;
  @Input() pattern: string;
  /**
   * Input type
   */
  @Input() type: "text" | "email" | "number" | "date" | "tel" | "password" = "text";
  /**
   * Marks fields as read only
   */
  @Input() readonly: boolean;
  /**
   * Custom error message for input
   */
  @Input() errorMessages: NInputErrorMessages;
  /**
   * Mark field as required (only styles)
   */
  @Input() required: boolean;
  /**
   * Remove all appear styles (only styles)
   */
  @Input() noappear: boolean;
  /**
   * maxlength property for input
   */
  @Input() maxlength: number;
  /**
   * Icon of the input
   */
  @Input()
  icon: string;
  @Input()
  iconSet: string;
  /**
   * id param for inner input field
   */
  @Input() inputId: string;
  @Output() focus = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();
  @ViewChild("input", {read: ElementRef, static: true})
  domInput: ElementRef;
  errorMessage: string;
  showClear: boolean;
  readonly wide: boolean;
  @ViewChild("container", {read: ElementRef, static: true})
  private container: ElementRef;
  private propagateChange: (_) => void;
  private propagateTouch: () => void;
  private compositionMode: boolean;
  private composing: boolean;
  private propagationDisabled: boolean;

  constructor(private renderer: Renderer2,
              private host: ElementRef,
              private cd: ChangeDetectorRef,
              private messageFactory: ErrorMessagesFactory,
              @Attribute("wide") wide: string) {

    this.wide = wide !== null;

    this.compositionMode = !_isAndroid();

  }

  /**
   * autocorrect property for input
   */
  private _autocorrect: "on" | "off" | null;

  // @ts-ignore
  @Input() set autocorrect(value: boolean) {
    if (typeof value === "undefined") {
      this._autocorrect = null;
    } else {
      this._autocorrect = value ? "on" : "off";
    }
  }

  get autocorrectProperty(): "on" | "off" |null {
    return this._autocorrect;
  }

  private _autocomplete: string|null = "on";

  /**
   * autocomplete property for input (true by default)
   */
  // @ts-ignore
  @Input() set autocomplete(val: boolean | string) {
    if (typeof val === "undefined") {
      this._autocomplete = null;
    }
    if (typeof val === "boolean") {
      this._autocomplete = val ? "on" : "off";
    } else if (typeof val === "string") {
      this._autocomplete = val;
    }
  }

  // @ts-ignore
  get autocompleteProperty(): string|null {
    return this._autocomplete;
  }

  private _disabled: boolean;

  get disabled(): boolean {
    return this._disabled;
  }

  @Input()
  set disabled(val: boolean) { // deprecated
    this.setDisabledState(val);
  }

  @Input()
  set isDisabled(val: boolean) {
    this.setDisabledState(val);
  }

  /**
   * User can clear input text
   * if "nofocus" is passed, then  field is not focused after cleaning
   */
  @Input()
  set clearable(val: boolean | "nofocus") {
    this.isClearable = val;
    this.showClear = !!this.domInput.nativeElement.value;
  }

  setErrors(errors: NControlsValidationErrors) {
    this.processErrors(errors);
  }

  setDisabledState(disabled: boolean) {
    this._disabled = disabled;
    this.renderer.setProperty(this.domInput.nativeElement, "disabled", disabled);
    this.cd.markForCheck();
  }

  /* public api */
  getInputElement(): HTMLInputElement {
    return this.domInput.nativeElement as HTMLInputElement;
  }

  ngOnInit() {
    this.invalidateStyles();
    this.processErrors(undefined);
  }

  disablePropagation() {
    this.propagationDisabled = true;
  }

  enablePropagaton() {
    this.propagationDisabled = false;
  }


  onChange(val) {
    if (this.propagationDisabled) {
      return;
    }
    if (this.propagateChange) {
      this.propagateChange(this.noTrim ? val.toString() : val.toString().trim());
    }
    this.invalidateStyles();

    if (this.isClearable) {
      this.showClear = !!val;
    }
  }

  onBlur() {
    if (this.propagateTouch) {
      this.propagateTouch();
    }
    this.blur.emit();
  }

  onMouseLeave() {
    this.renderer.removeClass(this.container.nativeElement, "n-input_hover");
  }

  onMouseOver() {
    this.renderer.addClass(this.container.nativeElement, "n-input_hover");
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  /* public api */
  setFocus() {
    this.domInput.nativeElement.focus();
  }

  writeValue(obj: any): void {
    obj = obj || "";
    const normalizedValue = this.noTrim ? obj.toString() : obj.toString().trim();
    this.renderer.setProperty(this.domInput.nativeElement, "value", normalizedValue);
    this.invalidateStyles();

    if (this.isClearable) {
      this.showClear = !!normalizedValue;
      this.cd.markForCheck();
    }
  }

  compositionStart() {
    this.composing = true;
  }

  compositionEnd(value: any) {
    this.composing = false;
    if (this.compositionMode) {
      this.onChange(value);
    }
  }

  clear() {
    this.renderer.setProperty(this.domInput.nativeElement, "value", "");
    this.onChange("");
    if (this.isClearable !== "nofocus") {
      this.setFocus();
    } else {
      this.onBlur();
    }

  }

  private invalidateStyles() {
    if (!this.domInput.nativeElement.value) {
      this.renderer.addClass(this.container.nativeElement, "n-input_empty");
    } else {
      this.renderer.removeClass(this.container.nativeElement, "n-input_empty");
    }
  }

  private processErrors(errors: NControlsValidationErrors) {
    if (!errors) {
      if (this.required) {
        this.errorMessage = buildErrorMessage({required: true}, this.messageFactory, this.errorMessages);
      } else {
        this.errorMessage = "";
      }
      return;
    }

    this.errorMessage = buildErrorMessage(errors, this.messageFactory, this.errorMessages);
  }
}

function _isAndroid(): boolean {
  return /android (\d+)/.test(navigator.userAgent.toLowerCase());
}
